<template>
  <NodeViewWrapper
    :class="[b(), alignClass, floatClass]"
    :data-align="node.attrs['data-align'] || undefined"
    :data-float="node.attrs['data-float'] || undefined"
  >
    <!-- Wrapper constrains panel width to match the image -->
    <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events -->
    <div
      :class="b('wrapper')"
      :style="containerStyle"
      @click="selectImage"
    >
      <!-- Image with selection border and resize handles -->
      <div :class="b('container', { selected })">
        <img
          ref="imgRef"
          :class="b('img')"
          :src="node.attrs.src"
          :alt="node.attrs.alt || ''"
          draggable="false"
        />

        <!-- Resize handles — visible only when selected -->
        <template v-if="selected">
          <div
            :class="b('handle', { nw: true })"
            @mousedown.prevent.stop="startResize('nw', $event)"
          />
          <div
            :class="b('handle', { ne: true })"
            @mousedown.prevent.stop="startResize('ne', $event)"
          />
          <div
            :class="b('handle', { sw: true })"
            @mousedown.prevent.stop="startResize('sw', $event)"
          />
          <div
            :class="b('handle', { se: true })"
            @mousedown.prevent.stop="startResize('se', $event)"
          />
        </template>
      </div>

      <!-- Settings panel — below image, same width -->
      <div
        v-if="selected"
        ref="toolbarRef"
        :class="b('panel')"
        @click.stop
      >
      <!-- Row 1: Alignment + Float toggle + Delete -->
      <div :class="b('panel-row')">
        <div :class="b('panel-group')">
          <span :class="b('panel-label')">Position:</span>
          <button
            :class="b('panel-btn', { active: node.attrs['data-align'] === 'left' })"
            title="Align left"
            @mousedown.prevent
            @click="setAlign('left')"
          >
            <AlignLeftOutlined />
          </button>
          <button
            :class="b('panel-btn', {
              active: !node.attrs['data-align'] || node.attrs['data-align'] === 'center',
            })"
            title="Align center"
            @mousedown.prevent
            @click="setAlign('center')"
          >
            <AlignCenterOutlined />
          </button>
          <button
            :class="b('panel-btn', { active: node.attrs['data-align'] === 'right' })"
            title="Align right"
            @mousedown.prevent
            @click="setAlign('right')"
          >
            <AlignRightOutlined />
          </button>
        </div>

        <div :class="b('panel-divider')" />

        <!-- Float toggle — enables text wrapping around the image -->
        <div :class="b('panel-group')">
          <button
            :class="b('panel-btn', { active: !!node.attrs['data-float'], wide: true })"
            title="Toggle text wrapping"
            @mousedown.prevent
            @click="toggleFloat"
          >
            <MenuOutlined />
            <span :class="b('panel-btn-text')">Wrap text</span>
          </button>
        </div>

        <div :class="b('panel-divider')" />

        <div :class="b('panel-group')">
          <button
            :class="b('panel-btn', { danger: true })"
            title="Delete image"
            @mousedown.prevent
            @click="deleteImg"
          >
            <DeleteOutlined />
          </button>
        </div>
      </div>

      <!-- Row 2: Alt text input -->
      <div :class="b('panel-row')">
        <div :class="b('panel-field')">
          <span :class="b('panel-label')">
            <FileImageOutlined />
            Alt
          </span>
          <input
            ref="altInputRef"
            v-model="altText"
            :class="b('panel-input')"
            placeholder="Image description for accessibility..."
            @keydown.enter="saveAlt"
            @blur="saveAlt"
          />
        </div>
      </div>

      <!-- Row 3: URL input -->
      <div :class="b('panel-row')">
        <div :class="b('panel-field')">
          <span :class="b('panel-label')">
            <LinkOutlined />
            URL
          </span>
          <input
            ref="srcInputRef"
            v-model="srcUrl"
            :class="b('panel-input')"
            placeholder="https://..."
            @keydown.enter="saveSrc"
            @blur="saveSrc"
          />
        </div>
      </div>
      </div>
    </div>
  </NodeViewWrapper>
</template>

<script>
import {
  defineComponent, ref, computed, watch, onBeforeUnmount,
} from 'vue';
import { NodeViewWrapper, nodeViewProps } from '@tiptap/vue-3';
import {
  AlignLeftOutlined,
  AlignCenterOutlined,
  AlignRightOutlined,
  FileImageOutlined,
  LinkOutlined,
  DeleteOutlined,
  MenuOutlined,
} from '@ant-design/icons-vue';
import bem from '@/utils/bem';

const b = bem('image-node');

export default defineComponent({
  name: 'ImageNodeView',

  components: {
    NodeViewWrapper,
    AlignLeftOutlined,
    AlignCenterOutlined,
    AlignRightOutlined,
    FileImageOutlined,
    LinkOutlined,
    DeleteOutlined,
    MenuOutlined,
  },

  props: nodeViewProps,

  setup(props) {
    const imgRef = ref(null);
    const toolbarRef = ref(null);
    const altInputRef = ref(null);
    const srcInputRef = ref(null);

    // Field values — synced with node attrs
    const altText = ref('');
    const srcUrl = ref('');

    // Resize state
    const isResizing = ref(false);
    const resizeStartX = ref(0);
    const resizeStartWidth = ref(0);
    const resizeHandle = ref('');
    const currentWidth = ref(null);

    // Container style — applies live width during resize
    const containerStyle = computed(() => {
      const w = isResizing.value ? currentWidth.value : props.node.attrs.width;
      if (!w) return {};
      return { width: typeof w === 'number' ? `${w}px` : w };
    });

    // Alignment class — position without text wrapping
    const alignClass = computed(() => {
      const align = props.node.attrs['data-align'];
      if (!align) return '';
      return `image-node_align-${align}`;
    });

    // Float class — enables text wrapping around the image
    const floatClass = computed(() => {
      const float = props.node.attrs['data-float'];
      if (!float) return '';
      return `image-node_float-${float}`;
    });

    // Sync input values when image is selected
    watch(() => props.selected, (isSelected) => {
      if (isSelected) {
        altText.value = props.node.attrs.alt || '';
        srcUrl.value = props.node.attrs.src || '';
      }
    });

    function selectImage() {
      const { getPos, editor } = props;
      editor.commands.setNodeSelection(getPos());
    }

    // --- Alignment (without text wrapping) ---
    function setAlign(value) {
      const current = props.node.attrs['data-align'];
      props.updateAttributes({ 'data-align': current === value ? null : value });
    }

    // --- Float toggle (text wrapping) ---
    function toggleFloat() {
      const currentFloat = props.node.attrs['data-float'];
      if (currentFloat) {
        // Turn off float
        props.updateAttributes({ 'data-float': null });
      } else {
        // Turn on float — direction matches current alignment
        const align = props.node.attrs['data-align'];
        const floatDir = align === 'right' ? 'right' : 'left';
        props.updateAttributes({ 'data-float': floatDir });
      }
    }

    // --- Alt text ---
    function saveAlt() {
      props.updateAttributes({ alt: altText.value });
    }

    // --- URL ---
    function saveSrc() {
      const trimmed = srcUrl.value.trim();
      if (trimmed && trimmed !== props.node.attrs.src) {
        props.updateAttributes({ src: trimmed });
      }
    }

    // --- Delete ---
    function deleteImg() {
      props.deleteNode();
    }

    // --- Resize ---
    function onResizeMove(event) {
      if (!isResizing.value) return;
      const deltaX = event.clientX - resizeStartX.value;
      const isLeftHandle = resizeHandle.value === 'nw' || resizeHandle.value === 'sw';
      const newWidth = isLeftHandle
        ? resizeStartWidth.value - deltaX
        : resizeStartWidth.value + deltaX;

      const editorEl = props.editor.view.dom;
      const maxWidth = editorEl ? editorEl.clientWidth : 1200;
      const clamped = Math.max(50, Math.min(newWidth, maxWidth));
      currentWidth.value = `${clamped}px`;
    }

    function onResizeEnd() {
      if (!isResizing.value) return;
      isResizing.value = false;
      props.updateAttributes({ width: currentWidth.value });
      document.removeEventListener('mousemove', onResizeMove);
      document.removeEventListener('mouseup', onResizeEnd);
    }

    function startResize(handle, event) {
      isResizing.value = true;
      resizeHandle.value = handle;
      resizeStartX.value = event.clientX;
      const imgEl = imgRef.value;
      resizeStartWidth.value = imgEl ? imgEl.offsetWidth : 300;
      currentWidth.value = `${resizeStartWidth.value}px`;

      document.addEventListener('mousemove', onResizeMove);
      document.addEventListener('mouseup', onResizeEnd);
    }

    onBeforeUnmount(() => {
      document.removeEventListener('mousemove', onResizeMove);
      document.removeEventListener('mouseup', onResizeEnd);
    });

    return {
      b,
      imgRef,
      toolbarRef,
      altInputRef,
      srcInputRef,
      altText,
      srcUrl,
      containerStyle,
      alignClass,
      floatClass,
      selectImage,
      setAlign,
      toggleFloat,
      saveAlt,
      saveSrc,
      deleteImg,
      startResize,
    };
  },
});
</script>

<style lang="scss">
/* Not scoped — NodeView renders inside ProseMirror DOM */

.image-node {
  position: relative;
  line-height: 0;

  /* Alignment — positions image without text wrapping */
  &_align-left {
    text-align: left;
  }

  &_align-center {
    display: flex;
    justify-content: center;
  }

  &_align-right {
    text-align: right;
  }

  /* Float — enables text wrapping around the image */
  &_float-left {
    float: left;
    margin-right: 16px;
    margin-bottom: 8px;
  }

  &_float-right {
    float: right;
    margin-left: 16px;
    margin-bottom: 8px;
  }

  &__wrapper {
    display: inline-block;
    max-width: 100%;
    position: relative;
  }

  &__container {
    position: relative;
    line-height: 0;

    &_selected {
      outline: 2px solid #1890ff;
      outline-offset: 2px;
      border-radius: 2px;
    }
  }

  &__img {
    width: 100%;
    height: auto;
    display: block;
  }

  /* Resize handles at corners */
  &__handle {
    position: absolute;
    width: 10px;
    height: 10px;
    background: #1890ff;
    border: 1.5px solid #fff;
    border-radius: 2px;
    z-index: 2;

    &_nw { top: -5px; left: -5px; cursor: nw-resize; }
    &_ne { top: -5px; right: -5px; cursor: ne-resize; }
    &_sw { bottom: -5px; left: -5px; cursor: sw-resize; }
    &_se { bottom: -5px; right: -5px; cursor: se-resize; }
  }

  /* Settings panel — overlay below image, does not push content */
  &__panel {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 8px;
    background: #1e1e1e;
    border: 1px solid #3a3a3a;
    border-radius: 8px;
    padding: 10px 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    z-index: 50;
    width: 100%;
    box-sizing: border-box;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
    line-height: normal;
  }

  &__panel-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__panel-group {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  &__panel-btn {
    background: transparent;
    border: 1px solid transparent;
    color: #bbb;
    padding: 6px 8px;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 14px;
    line-height: 1;
    transition: background 0.15s, color 0.15s;

    &:hover {
      background: #333;
      color: #fff;
    }

    &_active {
      background: #4a4a4a;
      color: #fff;
      border-color: #666;
    }

    &_wide {
      padding: 6px 10px;
    }

    &_danger {
      &:hover {
        background: #5c2020;
        color: #ff7875;
      }
    }
  }

  &__panel-btn-text {
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
  }

  &__panel-divider {
    width: 1px;
    height: 24px;
    background: #3a3a3a;
    margin: 0 4px;
    flex-shrink: 0;
  }

  &__panel-label {
    font-size: 12px;
    color: #888;
    white-space: nowrap;
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 44px;
  }

  &__panel-field {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    cursor: text;
  }

  &__panel-input {
    flex: 1;
    background: #2a2a2a;
    border: 1px solid #4a4a4a;
    color: #e0e0e0;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 13px;
    outline: none;
    width: 100%;

    &:focus {
      border-color: #1890ff;
    }

    &::placeholder {
      color: #666;
    }
  }
}
</style>
